import type { StateOrName } from "@uirouter/core/lib/state/interface";
import type { UIRouterReact } from "@uirouter/react/lib/core";

import Sentry from "@similarweb/sentry-logger";

import type { IRouterStateWithName } from "./router/IRouterState";

let router: UIRouterReact | null = null;
const ongoingLazyLoads = new Map<string, Promise<void>>();
const loadedStates = new Set<string>();
const uninitializedError = new Error("LazyLoadManager not initialized with router instance");

const init = (routerInstance: UIRouterReact) => {
    router = routerInstance;
};

const getState = (stateOrName: StateOrName): IRouterStateWithName => {
    if (!router) {
        Sentry.error(uninitializedError.message, uninitializedError);
        throw uninitializedError;
    }
    return router.stateService.get(stateOrName) as IRouterStateWithName;
};

const lazyLoadIfNeeded = async (stateOrName: StateOrName) => {
    if (!router) {
        Sentry.error(uninitializedError.message, uninitializedError);
        throw uninitializedError;
    }

    const wantedState = getState(stateOrName);
    if (wantedState?.lazyLoad) {
        const stateName = wantedState.name;

        if (loadedStates.has(stateName)) {
            return;
        }

        const existingLazyLoad = ongoingLazyLoads.get(stateName);
        if (existingLazyLoad !== undefined) {
            return existingLazyLoad;
        }

        const lazyLoadPromise = (async () => {
            try {
                await router.stateService.lazyLoad(stateOrName);
                loadedStates.add(stateName);
            } catch (error) {
                Sentry.error("Failed to lazy load state", error as Error, {
                    extra: {
                        stateOrName,
                        stateName,
                    },
                });
            } finally {
                ongoingLazyLoads.delete(stateName);
            }
        })();

        ongoingLazyLoads.set(stateName, lazyLoadPromise);
        return lazyLoadPromise;
    }
};

export const lazyLoadManager = {
    init,
    lazyLoadIfNeeded,
};
